<template>

    <div class="pa-0">

        <v-alert v-if="!displaySamplingCriteria" type="info" class="body-2" color="transparent">
            No sampling logic defined
        </v-alert>

        <v-card :flat="!isOrGroup"
                :class="disableEdit ? 'elevation-0' : 'elevation-4'"
                :color="isOrGroup ? 'grey darken-2' : color"
                v-if="displaySamplingCriteria">

            <v-container fluid class="pt-2 pb-0">
                <v-row no-gutters>
                    <v-col cols="12" class="pa-0">

                        <!-- DISPLAY MODE HEADER -->
                        <v-row dense v-if="disableEdit" row wrap>

                            <v-col cols="12" v-if="booleanOperationModel==='should'"
                                   class="caption grey--text text--lighten-1">
                                {{ booleanOperationText }} at least ({{ minimumShouldMatch }}) of the
                                following
                            </v-col>
                            <v-col v-else cols="12" class="caption grey--text text--lighten-1">
                                {{ booleanOperationText }}
                            </v-col>

                        </v-row>

                        <v-row v-else dense class="d-flex">

                            <div class="align-self-center pa-2">
                                <v-select
                                    v-model="booleanOperationModel"
                                    :items="booleanOperations"
                                    item-value="value"
                                    item-text="name"
                                    hide-details
                                    class="body-2"
                                    color="grey"
                                    solo-inverted
                                    @input="statementUpdated"
                                ></v-select>

                            </div>

                            <div class="flex-grow-1" v-if="!disableEdit"></div>

                            <!-- COMPACT TOGGLE -->
                            <v-tooltip v-if="!isOrGroup && !disableEdit" top color="primary" max-width="300">
                                <template v-slot:activator="{on}">

                                    <v-sheet color="transparent" v-on="on">
                                        <v-switch v-model="compactSwitchModel"
                                                  class="align-self-center mr-16"
                                                  label="Compact"
                                                  @change="compactSwitchChanged"
                                        ></v-switch>
                                    </v-sheet>

                                </template>
                                <span>Controls whether a compact mode is used. We will remember this setting for all your segments for now on</span>
                            </v-tooltip>

                            <!-- MINIMUM SHOULD MATCH -->
                            <!--   <v-col cols="2" v-if="booleanOperationModel==='should' && !disableEdit && !isOrGroup">

                                   <v-text-field
                                       class="mt-2"
                                       v-model="minimumShouldMatch"
                                       label="Minimum should match"
                                       @blur="statementUpdated"
                                   ></v-text-field>

                               </v-col>-->

                        </v-row>

                    </v-col>

                    <!-- CONDITIONS -->
                    <v-col cols="12" v-for="(condition,index) in conditions" :key="condition.id"
                           class="pl-0 pt-0"
                           v-if="displaySamplingCriteria">

                        <condition
                            :can-be-deleted="conditions.length>1"
                            @deleted="removeCondition"
                            @changed="conditionChanged"
                            :data-variables="dataVariables"
                            :data="condition"
                            :is-or-group="isOrGroup"
                            :disable-edit="disableEdit"
                            :compact="compactSwitchModel"
                        ></condition>

                        <template v-if="displayAndOr">
                            <div class="pt-2" v-if="!isOrGroup && conditions.length>(index+1)">AND</div>
                            <div class="pt-2" v-if="isOrGroup && conditions.length>(index+1)">OR</div>
                        </template>

                        <v-chip v-if="!disableEdit && !compactSwitchModel && index < conditions.length - 1"
                                label
                                class="ml-1"
                                color="blue-grey darken-2"
                                small>
                            {{getLogicalOperator()}}
                        </v-chip>

                    </v-col>

                    <!-- SUB QUERIES -->
                    <v-col cols="12" v-for="subQuery in subQueries" :key="subQuery.id">

                        <v-chip v-if="!disableEdit && !compactSwitchModel"
                                label
                                class="ml-1"
                                color="blue-grey darken-2"
                                small>
                            {{getLogicalOperator()}}
                        </v-chip>

                        <v-card flat :color="color" style="border: 1px solid red">
                            <v-card-text class="pa-1">
                                <div v-if="displayAndOr" class="pt-1 pb-1">AND</div>

                                <v-tooltip top color="primary">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-if="!disableEdit"
                                               small
                                               class="mt-2"
                                               absolute
                                               dark
                                               fab
                                               right
                                               icon
                                               color="blue-grey darken-1"
                                               @click="removeSubQuery(subQuery.id)"
                                        >
                                            <v-icon medium color="white">mdi-close</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Remove this group</span>
                                </v-tooltip>

                                <statement
                                    :query="subQuery"
                                    @changed="subQueryChanged"
                                    :data-variables="dataVariables"
                                    :is-or-group="true"
                                    :disable-edit="disableEdit"
                                ></statement>

                            </v-card-text>
                        </v-card>

                    </v-col>

                    <!-- ACTION BUTTONS -->
                    <v-col cols="12" v-if="!disableEdit" class="pl-2 pt-2 pb-3">

                        <v-tooltip top color="primary" open-delay="500">
                            <template v-slot:activator="{ on }">
                                <v-btn :color="orGroupAdded ? 'secondary' : 'primary'"
                                       small
                                       v-on="on"
                                       @click="addNewCondition()">
                                    ADD CONDITION
                                </v-btn>
                            </template>
                            <div class="text-center">
                                Click here to add more conditions
                            </div>
                        </v-tooltip>

                        <v-tooltip top color="primary" open-delay="500">
                            <template v-slot:activator="{ on }">
                                <v-btn v-if="!isOrGroup"
                                       color="secondary"
                                       v-on="on"
                                       small
                                       @click="addOrGroup()">
                                    ADD SUB GROUP
                                </v-btn>
                            </template>
                            <div class="test-center">
                                Click to add sub group condition block
                            </div>
                        </v-tooltip>

                    </v-col>

                    <!-- DEBUG -->
                    <v-col cols="12" v-if="debug">
                        <v-card color="grey darken-4">
                            <v-card-text>
                                CONDITIONS<br>
                                <vue-json-pretty :data="conditions"></vue-json-pretty>
                                SUBQUERIES<br>
                                <vue-json-pretty :data="subQueries"></vue-json-pretty>
                            </v-card-text>

                        </v-card>
                    </v-col>

                </v-row>
            </v-container>

        </v-card>
    </div>

</template>

<script>

import Condition from './Condition.vue';
import Statement from './Statement.vue';
import VueJsonPretty from 'vue-json-pretty';
import {v4 as uuid} from 'uuid';

export default {

    name: 'statement',

    components: {
        Condition,
        Statement,
        VueJsonPretty
    },

    props: {
        dataVariables: {
            required: true,
            type: Array,
        },
        query: {
            required: true,
            type: Object
        },
        isOrGroup: {
            required: false,
            default: false,
            type: Boolean,
        },
        disableEdit: {
            required: false,
            type: Boolean,
            default: false
        },
        color: {
            required: false,
            type: String,
            default: 'grey darken-3'
        },
        displayAndOr: {
            required: false,
            type: Boolean,
            default: false
        }
    },

    watch: {
        query(query) {
            this.prepareFromStore();
        }
    },

    computed: {
        booleanOperationText() {

            let found = null;

            if (!this.disableEdit) {
                found = this.booleanOperations.find((operation) => {
                    return operation.value === this.booleanOperationModel;
                });
            } else {
                found = this.booleanOperationsDisplay.find((operation) => {
                    return operation.value === this.booleanOperationModel;
                });
            }

            if (found) return found.name;
        },

        displaySamplingCriteria() {
            if (!this.query) return false;
            if (!this.disableEdit) return true;

            if (!this.conditions) return false;

            if (this.conditions.length === 1) {
                return this.conditions[0].operation && this.conditions[0].dataVariableId;
            }

            return true;
        },
    },
    data() {
        return {
            debug: false,
            minimumShouldMatch: 1,
            orGroupAdded: false,

            conditions: [],
            booleanOperationModel: 'must',
            booleanOperations: [
                {value: 'must', name: 'Match all of the following'},
                {value: 'must_not', name: 'Exclude all of the following'},
                {value: 'should', name: 'Match at least one of the following'},
                //{value: 'should_not', name: 'OR group'}
            ],
            booleanOperationsDisplay: [
                {value: 'must', name: 'Match all of the following'},
                {value: 'must_not', name: 'Exclude all of the following'},
                {value: 'should', name: 'Match at least one of the following'},
                //{value: 'should_not', name: 'Condition matches one of the following'}
            ],
            subQueries: [],
            compactSwitchModel: false,
        }
    },

    mounted() {
        this.prepareFromStore();
        this.statementUpdated();

        // Add empty condition for new queries
        if (!this.query.conditions) {
            this.addNewCondition();
        }

        const compactSetting = localStorage.getItem('segmentBuilderCompact');

        if (compactSetting) {
            this.compactSwitchModel = JSON.parse(compactSetting);
        }

        Bus.$on('segment-builder-compact', (bool) => {
           this.compactSwitchModel = bool;
        });
    },

    methods: {

        getLogicalOperator() {
            if (this.booleanOperationModel === "must" || this.booleanOperationModel === "must_not") {
                return 'AND';
            }  else {
                return 'OR';
            }
        },

        prepareFromStore() {

            this.conditions = [];
            this.subQueries = [];

            this.id = this.query.id ? this.query.id : uuid();

            if (this.isOrGroup) {
                this.booleanOperationModel = 'should';
                this.booleanOperationToggleModel = 1;
                this.minimumShouldMatch = 1;
            } else {
                if (this.query.booleanOperation === 'must') {
                    this.booleanOperationToggleModel = 0;
                } else {
                    this.booleanOperationToggleModel = 1;
                }
                this.booleanOperationModel = this.query.booleanOperation;

                // Do not accept null from saved query
                this.minimumShouldMatch = this.query.minimumShouldMatch ? this.query.minimumShouldMatch : 1;
            }

            if (this.query.conditions) {
                this.query.conditions.forEach((condition) => {
                    this.addCondition(condition);
                });
            }

            if (this.query.subQueries) {
                this.query.subQueries.forEach((subQuery) => {
                    this.addSubQuery(subQuery);
                });
            }
        },

        statementUpdated() {

            let statement = {
                id: this.id,
                conditions: this.conditions,
                subQueries: this.subQueries,
                booleanOperation: this.booleanOperationModel,
                minimumShouldMatch: this.booleanOperationModel === 'should' ? this.minimumShouldMatch : null
            };

            // Scan for unfinished conditions
            let bad = this.conditions.find((condition) => {
                return !condition.dataVariableId;
            });

            if (!bad && this.conditions.length > 0) {
                this.$emit('changed', statement);
            }
        },

        booleanOperationChanged(operation) {
            if (operation === 0) {
                this.booleanOperationModel = 'must';
            } else {
                this.booleanOperationModel = 'should';
            }
            this.statementUpdated();
        },

        addNewCondition() {
            let condition = {
                id: uuid(),
                operation: 'match',
                dataVariableId: null,
                value: null
            };

            this.addCondition(condition);
        },

        addCondition(condition) {
            if (!condition.id) condition.id = uuid();
            this.conditions.push(Object.assign({}, condition));
        },

        removeCondition(conditionId) {

            if (this.conditions.length === 1) {
                Bus.$emit('flash-message', {type: "error", message: "Last condition cannot be removed"});
                return;
            }

            let index = this.conditions.findIndex((condition) => {
                return condition.id === conditionId
            });
            this.conditions.splice(index, 1);
            this.statementUpdated();
        },

        conditionChanged(data) {

            let found = this.conditions.find((condition) => {
                return condition.id === data.id;
            });

            if (!found) {
                Bus.$emit('flash-message', {
                    type: 'error',
                    message: 'System error! Unable to locate condition!'
                });
            } else {
                found = Object.assign(found, data);
            }
            this.statementUpdated();
        },

        addOrGroup() {

            this.orGroupAdded = true;

            let subQuery = {
                id: uuid(),
                booleanOperation: "must",
                minimumShouldMatch: 1,
                conditions: [
                    {
                        id: uuid(),
                        dataVariableId: null,
                        operation: 'match',
                        value: null
                    }
                ],
                subQueries: []
            };

            this.addSubQuery(subQuery);
        },

        addSubQuery(subQuery) {
            this.subQueries.push(subQuery);
        },

        removeSubQuery(subQueryId) {

            let index = this.subQueries.findIndex((subQuery) => {
                return subQuery.id === subQueryId
            });

            this.subQueries.splice(index, 1);

            this.statementUpdated();
        },

        subQueryChanged(data) {

            let found = this.subQueries.find((subQuery) => {
                return subQuery.id === data.id;
            });

            if (!found) {
                alert("Sub query changed but not found!");
            }

            found = Object.assign(found, data);

            this.statementUpdated();
        },

        compactSwitchChanged(bool) {
            localStorage.segmentBuilderCompact = JSON.stringify(bool);
            Bus.$emit('segment-builder-compact', bool);
        }
    }
}
</script>
